import { useMemo } from 'react';
import { useGetTeamCompetitionsQuery, useGetMemberSummaryQuery } from '../state/api';
import { selectAllTeamCompetitions, selectAllMembers } from '../state/entitySelectors';
import { useAppSelector } from './reduxHooks';
import type { TeamCompetition, MemberSummary } from '../types/api.types';

/**
 * Return type for useTeamCompetitionData hook
 */
export interface TeamCompetitionDataResult {
  /** @deprecated Use competitions instead */
  teamCompetitions: {
    competitions: Record<string, TeamCompetition>;
  };
  /** @deprecated Use members instead */
  memberSummary: {
    members: MemberSummary[];
  };
  /** Array of members (normalized via entity adapter) */
  members: MemberSummary[];
  /** Dictionary of competitions (keyed by id) */
  competitions: Record<string, TeamCompetition>;
}

/**
 * Custom hook to fetch and provide team competition and member summary data.
 * Encapsulates the common pattern of fetching team competitions and member summary
 * used across multiple containers.
 *
 * Now uses RTK Query with entity adapters for normalized data access.
 *
 * @returns An object containing team competitions and member data
 */
export const useTeamCompetitionData = (): TeamCompetitionDataResult => {
  // Fetch data with RTK Query (handles caching)
  // Include competitors to get captain information
  useGetTeamCompetitionsQuery({ withCompetitors: true });
  useGetMemberSummaryQuery();

  // Get normalized data from entity selectors
  // Pass true to select from the cache with withCompetitors: true
  const competitionsArray = useAppSelector((state) => selectAllTeamCompetitions(state, true));
  const members = useAppSelector(selectAllMembers);

  // Convert competitions array to map for backward compatibility
  // Key by id
  const competitions = useMemo(() => {
    const map: Record<string, TeamCompetition> = {};
    if (competitionsArray) {
      competitionsArray.forEach((comp) => {
        // Key by id
        if (comp.id != null) {
          map[comp.id] = comp;
        }
      });
    }
    return map;
  }, [competitionsArray]);

  // Keep backward compatibility by also returning the old format
  return {
    teamCompetitions: { competitions },
    memberSummary: { members },
    members,
    competitions,
  };
};
